/**
 * Zustaende des VoD-Players.
 *
 * FORWARD/REWIND sind eigene Zustaende und nicht nur "PLAYING mit Tempo": die
 * Bedienleiste zeigt waehrend des Spulens ein anderes Symbol, und die
 * Tastenlogik unterscheidet, ob PLAY das Spulen beendet oder aus der Pause
 * heraus startet.
 */
export const PLAYER_STATE = {
  IDLE: 'IDLE',
  PLAYING: 'PLAYING',
  PAUSED: 'PAUSED',
  FORWARD: 'FORWARD',
  REWIND: 'REWIND',
  STOPPED: 'STOPPED',
  ENDED: 'ENDED',
  ERROR: 'ERROR',
};

/**
 * Tempostufen beim Spulen. Jeder Druck auf Vorwaerts/Rueckwaerts geht eine
 * Stufe weiter, nach der hoechsten Stufe faengt es wieder bei der ersten an -
 * so wie es die Fernbedienungen von Samsung und LG von Haus aus machen.
 */
export const TRICK_SPEEDS = [2, 4, 8, 16, 32];

/** Sprungweite von LINKS/RECHTS in Sekunden. */
export const SEEK_STEP = 15;

/** Takt des Spulens in Millisekunden. */
const TRICK_TICK_MS = 400;

/**
 * Abstand zum Dateiende, auf den das Vorspulen begrenzt wird.
 *
 * Genau auf duration zu springen beendet auf beiden Plattformen den Stream und
 * loest 'ended' aus - der Gast landet also mitten im Spulen am Ende des Films,
 * ohne noch einmal anhalten zu koennen.
 */
const END_GUARD = 0.5;

/**
 * Steuerung des VoD-Players fuer Samsung (Tizen 6 - 9) und LG (webOS 5 - 25).
 *
 * Gearbeitet wird auf dem HTML5-<video>-Element, NICHT auf webapis.avplay bzw.
 * hcap.Media. Beide Firmware-Schnittstellen belegen dieselbe Dekoder-Instanz
 * wie der Tuner (helpers/TV/*) - ein zweiter Player daneben liefert je nach
 * Geraet Bild ohne Ton oder gar nichts. Der HTML5-Weg spielt MP4 und HLS auf
 * allen genannten Firmware-Staenden und kommt dem Tuner nicht in die Quere.
 *
 * SPULEN LAEUFT UEBER SPRUNGSUCHLAUF, NICHT UEBER playbackRate.
 * playbackRate ist auf Hospitality-Geraeten unzuverlaessig: Tizen nimmt den
 * Wert an, ignoriert ihn aber bei HLS; LG begrenzt ihn je nach Firmware auf
 * 2.0, und negative Werte kennt keine der beiden Plattformen - Rueckwaerts
 * waere damit ohnehin nicht moeglich. Stattdessen wird das Video angehalten und
 * currentTime im Takt weitergesetzt. Das ergibt auf jedem Geraet exakt dieselbe
 * Geschwindigkeit und braucht keine Fallunterscheidung nach Firmware.
 *
 * Die Schrittweite richtet sich nach der TATSAECHLICH vergangenen Zeit, nicht
 * nach dem Soll-Takt: bei langsamem Suchlauf (grosse Dateien, HLS) kommen die
 * Takte verzoegert, und mit fester Schrittweite waere das Spulen dann je nach
 * Quelle unterschiedlich schnell.
 */
export default class VodPlayer {
  /**
   * @param {Object} [options]
   * @param {Function} [options.onChange] - Rueckruf bei jeder Zustandsaenderung
   */
  constructor(options = {}) {
    this.video = null;
    this.state = PLAYER_STATE.IDLE;
    this.speedIndex = -1;
    this.onChange = options.onChange || function() {};

    this.trickTimer = null;
    this.trickLastTick = 0;

    this.handleTimeUpdate = this.handleTimeUpdate.bind(this);
    this.handleEnded = this.handleEnded.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handlePlaying = this.handlePlaying.bind(this);
    this.handlePause = this.handlePause.bind(this);
    this.tick = this.tick.bind(this);
  }

  // ---- An- und Abmelden ----------------------------------------------------

  /**
   * Bindet die Steuerung an ein <video>-Element.
   * Ein zuvor gebundenes Element wird vorher sauber geloest.
   */
  attach(video) {
    if (!video) return;
    if (this.video === video) return;
    this.detach();
    this.video = video;

    // playsinline verhindert, dass der Chrome-Browser (Testfall der
    // Entwicklung) auf einen eigenen Vollbild-Player umschaltet. Auf den TVs
    // ohne Wirkung, aber auch ohne Schaden.
    this.video.playsInline = true;
    this.video.setAttribute('playsinline', '');

    // Ohne preload holt Tizen erst beim ersten play() Daten - der Start dauert
    // dann sichtbar laenger als auf LG.
    if (!this.video.getAttribute('preload')) this.video.preload = 'auto';

    video.addEventListener('timeupdate', this.handleTimeUpdate);
    video.addEventListener('durationchange', this.handleTimeUpdate);
    video.addEventListener('loadedmetadata', this.handleTimeUpdate);
    video.addEventListener('ended', this.handleEnded);
    video.addEventListener('error', this.handleError);
    video.addEventListener('playing', this.handlePlaying);
    video.addEventListener('pause', this.handlePause);
    this.emit();
  }

  /** Loest die Steuerung vom Element und raeumt Takt und Ereignisse ab. */
  detach() {
    this.stopTrick();
    const video = this.video;
    if (!video) return;
    video.removeEventListener('timeupdate', this.handleTimeUpdate);
    video.removeEventListener('durationchange', this.handleTimeUpdate);
    video.removeEventListener('loadedmetadata', this.handleTimeUpdate);
    video.removeEventListener('ended', this.handleEnded);
    video.removeEventListener('error', this.handleError);
    video.removeEventListener('playing', this.handlePlaying);
    video.removeEventListener('pause', this.handlePause);
    this.video = null;
  }

  // ---- Grundfunktionen -----------------------------------------------------

  /**
   * Startet die Wiedergabe in normalem Tempo. Beendet ein laufendes Spulen.
   * @returns {Promise<boolean>} false, wenn das Geraet den Start verweigert hat
   */
  async play() {
    if (!this.video) return false;
    this.stopTrick();
    try {
      // play() liefert auf aelteren webOS-Staenden KEIN Promise. await auf
      // undefined ist unschaedlich, ein .catch() daran waere ein Fehler.
      await this.video.play();
      this.setState(PLAYER_STATE.PLAYING);
      return true;
    } catch (e) {
      console.log('[VodPlayer] play abgelehnt', e);
      this.setState(PLAYER_STATE.PAUSED);
      return false;
    }
  }

  /** Haelt an, ohne die Position zu verlieren. Beendet ein laufendes Spulen. */
  pause() {
    if (!this.video) return false;
    this.stopTrick();
    try {
      this.video.pause();
    } catch (e) {
      console.log('[VodPlayer] pause', e);
    }
    this.setState(PLAYER_STATE.PAUSED);
    return true;
  }

  /**
   * Play/Pause auf einer Taste - das Verhalten der MediaPlayPause-Taste und
   * der OK-Taste.
   *
   * Waehrend des Spulens ist es KEIN Umschalter, sondern beendet das Spulen und
   * spielt weiter. Sonst muesste der Gast zweimal druecken, um aus dem
   * Suchlauf zurueck in die Wiedergabe zu kommen.
   */
  togglePlayPause() {
    if (this.isTrick) return this.play();
    if (this.state === PLAYER_STATE.PLAYING) return this.pause();
    return this.play();
  }

  /**
   * Beendet die Wiedergabe und stellt auf den Anfang zurueck.
   *
   * Was danach passiert, entscheidet die Seite (Bedienleiste stehen lassen oder
   * zurueck in die Liste) - der Player navigiert nicht selbst.
   */
  stop() {
    if (!this.video) return false;
    this.stopTrick();
    try {
      this.video.pause();
      this.video.currentTime = 0;
    } catch (e) {
      console.log('[VodPlayer] stop', e);
    }
    this.setState(PLAYER_STATE.STOPPED);
    return true;
  }

  // ---- Springen ------------------------------------------------------------

  /** Springt auf eine absolute Position (Sekunden), begrenzt auf die Datei. */
  seekTo(seconds) {
    if (!this.video) return false;
    const duration = this.duration;
    let target = Number(seconds);
    if (!isFinite(target)) return false;
    if (target < 0) target = 0;
    if (duration && target > duration - END_GUARD) target = duration - END_GUARD;
    try {
      this.video.currentTime = target;
    } catch (e) {
      console.log('[VodPlayer] seekTo', e);
      return false;
    }
    this.emit();
    return true;
  }

  /** Springt relativ zur aktuellen Position (LINKS/RECHTS). */
  seekBy(seconds) {
    if (!this.video) return false;
    return this.seekTo(this.video.currentTime + Number(seconds || 0));
  }

  // ---- Spulen --------------------------------------------------------------

  /**
   * Vorspulen. Erster Druck 2x, jeder weitere eine Stufe hoeher, nach der
   * hoechsten Stufe wieder 2x. Aus dem Rueckspulen heraus beginnt die Zaehlung
   * neu bei 2x vorwaerts.
   */
  fastForward() {
    return this.startTrick(PLAYER_STATE.FORWARD);
  }

  /** Rueckspulen, gleiche Stufenlogik wie fastForward(). */
  rewind() {
    return this.startTrick(PLAYER_STATE.REWIND);
  }

  startTrick(direction) {
    if (!this.video) return false;

    // Richtungswechsel faengt bei der kleinsten Stufe an, dieselbe Richtung
    // schaltet eine Stufe hoeher. Gemerkt wird der Index VOR setState, denn
    // setState setzt ihn fuer alle Nicht-Spul-Zustaende zurueck.
    const nextIndex =
      this.state === direction ? (this.speedIndex + 1) % TRICK_SPEEDS.length : 0;

    // Angehalten wird bewusst: der Sprungsuchlauf setzt currentTime selbst, ein
    // nebenher laufendes Video wuerde die Schrittweite verfaelschen und den Ton
    // zerhacken.
    try {
      if (!this.video.paused) this.video.pause();
    } catch (e) {
      console.log('[VodPlayer] pause vor Spulen', e);
    }

    this.state = direction;
    this.speedIndex = nextIndex;
    if (!this.trickTimer) {
      this.trickLastTick = Date.now();
      this.trickTimer = setInterval(this.tick, TRICK_TICK_MS);
    }
    this.emit();
    return true;
  }

  /** Beendet den Spul-Takt. Den Zustand setzt der Aufrufer. */
  stopTrick() {
    if (this.trickTimer) {
      clearInterval(this.trickTimer);
      this.trickTimer = null;
    }
  }

  tick() {
    const video = this.video;
    if (!video || !this.isTrick) {
      this.stopTrick();
      return;
    }

    // Laeuft noch ein Sprung, wird dieser Takt uebersprungen - OHNE die Uhr
    // weiterzustellen. Der naechste ausgefuehrte Schritt deckt die
    // uebersprungene Zeit mit ab, das Spulen bleibt also gleich schnell.
    if (video.seeking) return;

    const now = Date.now();
    const elapsed = (now - this.trickLastTick) / 1000;
    this.trickLastTick = now;

    const direction = this.state === PLAYER_STATE.REWIND ? -1 : 1;
    const target = video.currentTime + direction * this.speed * elapsed;

    if (direction < 0 && target <= 0) {
      // Am Anfang angekommen: normal weiterspielen statt am Nullpunkt kleben.
      this.seekTo(0);
      this.play();
      return;
    }

    const duration = this.duration;
    if (direction > 0 && duration && target >= duration - END_GUARD) {
      // Am Ende angekommen: anhalten statt den Stream zu beenden, damit der
      // Gast von dort noch zurueckspulen kann.
      this.seekTo(duration - END_GUARD);
      this.pause();
      return;
    }

    this.seekTo(target);
  }

  // ---- Zustand -------------------------------------------------------------

  get isTrick() {
    return (
      this.state === PLAYER_STATE.FORWARD || this.state === PLAYER_STATE.REWIND
    );
  }

  /** Tempofaktor, 1 solange nicht gespult wird. */
  get speed() {
    if (this.speedIndex < 0) return 1;
    return TRICK_SPEEDS[this.speedIndex] || 1;
  }

  get currentTime() {
    return this.video ? this.video.currentTime || 0 : 0;
  }

  /**
   * Laufzeit in Sekunden, 0 wenn unbekannt.
   *
   * Live-HLS meldet Infinity, und NaN kommt vor, solange die Kopfdaten fehlen -
   * beides wuerde die Fortschrittsleiste zerlegen.
   */
  get duration() {
    if (!this.video) return 0;
    const duration = this.video.duration;
    if (!isFinite(duration) || isNaN(duration)) return 0;
    return duration;
  }

  /** Fortschritt in Prozent (0 - 100). */
  get progress() {
    const duration = this.duration;
    if (!duration) return 0;
    return Math.min(100, Math.max(0, (this.currentTime / duration) * 100));
  }

  setState(state) {
    if (state !== PLAYER_STATE.FORWARD && state !== PLAYER_STATE.REWIND) {
      this.speedIndex = -1;
    }
    this.state = state;
    this.emit();
  }

  emit() {
    this.onChange({
      state: this.state,
      speed: this.speed,
      currentTime: this.currentTime,
      duration: this.duration,
      progress: this.progress,
    });
  }

  // ---- Ereignisse des <video>-Elements -------------------------------------

  handleTimeUpdate() {
    this.emit();
  }

  handlePlaying() {
    // Nicht waehrend des Spulens: der Sprungsuchlauf haelt das Video an, ein
    // spaeter eintreffendes 'playing' wuerde den Zustand sonst zurueckdrehen.
    if (this.isTrick) return;
    if (this.state !== PLAYER_STATE.PLAYING) this.setState(PLAYER_STATE.PLAYING);
  }

  handlePause() {
    if (this.isTrick) return;
    if (this.state === PLAYER_STATE.ENDED) return;
    if (this.state === PLAYER_STATE.STOPPED) return;
    if (this.state !== PLAYER_STATE.PAUSED) this.setState(PLAYER_STATE.PAUSED);
  }

  handleEnded() {
    this.stopTrick();
    this.setState(PLAYER_STATE.ENDED);
  }

  handleError(event) {
    const error = this.video && this.video.error;
    console.log(
      '[VodPlayer] Fehler',
      error && error.code,
      error && error.message,
      event
    );
    this.stopTrick();
    this.setState(PLAYER_STATE.ERROR);
  }
}

/**
 * Sekunden als h:mm:ss bzw. m:ss.
 * Unbekannte Werte ergeben '--:--' statt 'NaN:NaN'.
 */
export const formatTime = (seconds) => {
  const total = Math.floor(Number(seconds));
  if (!isFinite(total) || isNaN(total) || total < 0) return '--:--';
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = (value) => (value < 10 ? '0' + value : String(value));
  if (hours > 0) return hours + ':' + pad(minutes) + ':' + pad(secs);
  return minutes + ':' + pad(secs);
};



// WEBPACK FOOTER //
// ./src/helpers/VodPlayer/index.js